import React, { ReactNode, useEffect, useRef } from 'react';
import { Formik, FormikProvider, useFormikContext, FormikProps, FormikHelpers, FormikValues, FormikContextType } from 'formik';
import { ReportInstanceScope } from 'domain/entity/reportInstance';
import { PeriodSelectorPeriods } from 'adapter/in/common/form/types';
import styled from 'styled-components';

export type ReportTemplateFormFields = {
    language: string;
    location: string;
    period: string;
    logo: string;
    locationTags: string[];
    scope: ReportInstanceScope;
    userIds: string[];
    name: string;
};

export type AutomationFormFields = {
    language: string;
    locationIds: string[];
    templateId: string;
    locationTags: string[];
    scope: ReportInstanceScope;
    startingDate: string;
    periodicityValue: string;
    periodicityPeriod: PeriodSelectorPeriods | '';
    byMailUsers: string[];
    endDate: string;
    delayDays: string;
    userIds: string[];
    logo: string;
};

type GenericFormikAdapterProps<T extends FormikValues> = {
    initialValues: T;
    onSubmit: (values: T, helpers: FormikHelpers<T>) => void;
    onChange: (criteria?: any) => void;
    dependenciesSelector: (values: T) => unknown[];
    children: ReactNode;
    validationSchema?: any;
    className?: string;
};

const StyledReportTemplateForm = styled.form`
    height: 100%;
    width: 100%;
`;

export const GenericFormikAdapter = <T extends FormikValues>({
    initialValues,
    onSubmit,
    onChange,
    dependenciesSelector,
    children,
    validationSchema,
    className,
}: GenericFormikAdapterProps<T>): JSX.Element => (
    <Formik<T>
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, helpers) => {
            onSubmit(values, helpers);
            helpers.setSubmitting(false);
        }}
        validateOnChange
    >
        {(formikProps: FormikProps<T>) => (
            <StyledReportTemplateForm
                className={className}
                onSubmit={formikProps.handleSubmit}
                onKeyDown={(e: any) => {
                    const target = e.target as HTMLElement;
                    if (e.key === 'Enter' && target.tagName !== 'TEXTAREA') {
                        e.preventDefault();
                    }
                }}
            >
                <FormSubmitOnChangeGeneric formikProps={formikProps} onChange={onChange} dependenciesSelector={dependenciesSelector} />
                <FormikProvider value={formikProps}>{children}</FormikProvider>
            </StyledReportTemplateForm>
        )}
    </Formik>
);

type FormSubmitOnChangeGenericProps<T extends FormikValues> = {
    formikProps: FormikProps<T>;
    onChange: (values?: T) => void;
    dependenciesSelector: (values: T) => unknown[];
};

export const FormSubmitOnChangeGeneric = <T extends FormikValues>({
    formikProps,
    onChange,
    dependenciesSelector,
}: FormSubmitOnChangeGenericProps<T>): JSX.Element => {
    const hasMounted = useRef(false);
    const dependencies = dependenciesSelector(formikProps.values);

    useEffect(() => {
        if (hasMounted.current && formikProps.isValid) {
            onChange(formikProps.values);
        } else {
            hasMounted.current = true;
        }
        // eslint-disable-next-line
    }, [...dependencies, formikProps.isValid]);

    return <></>;
};

export const useTypedFormikContext = <T extends FormikValues>(): FormikContextType<T> => useFormikContext<T>();
